import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { uGet, uPut, uDelete } from "../../utils/userApi";
import {
  C,
  Input,
  Select,
  Btn,
  Table,
  Pagination,
  Modal,
  EmptyState,
  LoadingSpinner,
} from "./AdminPanel";
import "../../styles/user.css";

// React Icons imports
import {
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiShield,
  FiFilter,
  FiX,
  FiRefreshCw,
  FiMoreVertical,
  FiCheck,
  FiAlertCircle,
  FiTrendingUp,
  FiUsers as FiUsersIcon,
} from "react-icons/fi";
import {
  FaUserCircle,
  FaUserShield,
  FaUser,
  FaCrown,
  FaRegUser,
} from "react-icons/fa";
import {
  MdAdminPanelSettings,
  MdPerson,
  MdEmail,
  MdPhone,
  MdDateRange,
  MdVerified,
  MdOutlineVerified,
} from "react-icons/md";

const ROLE_META = {
  admin: {
    label: "Admin",
    icon: <FaCrown />,
    color: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.1)",
    description: "Full access to admin panel and management features",
  },
  user: {
    label: "User",
    icon: <FaUser />,
    color: "#10b981",
    bg: "rgba(16, 185, 129, 0.1)",
    description: "Regular customer access",
  },
};

// User Card Component for Mobile View
function UserCardMobile({ user, onEdit, onDelete }) {
  const roleMeta = ROLE_META[user.role] || ROLE_META.user;

  return (
    <div className="user-card-enhanced">
      <div className="user-card-header">
        <div className={`user-card-avatar ${user.role}`}>
          {user.name?.charAt(0)?.toUpperCase()}
        </div>
        <div className="user-card-info">
          <div className="user-card-name">{user.name}</div>
          <div className="user-card-email">
            <FiMail size={12} /> {user.email}
          </div>
        </div>
        <div className={`role-badge-enhanced ${user.role}`}>
          {roleMeta.icon}
          {roleMeta.label}
        </div>
      </div>

      <div className="user-card-details">
        <div className="detail-item">
          <FiPhone size={12} />
          <span>{user.phone || "Not provided"}</span>
        </div>
        <div className="detail-item">
          <FiCalendar size={12} />
          <span>{new Date(user.createdAt).toLocaleDateString("en-IN")}</span>
        </div>
      </div>

      <div className="user-card-actions">
        <button className="user-action-btn edit" onClick={() => onEdit(user)}>
          <FiEdit2 size={14} /> Edit
        </button>
        <button
          className="user-action-btn delete"
          onClick={() => onDelete(user.id)}
        >
          <FiTrash2 size={14} /> Delete
        </button>
      </div>
    </div>
  );
}

// Role Option Component for Modal
function RoleOption({ role, selected, onSelect }) {
  const meta = ROLE_META[role];
  return (
    <button
      className={`role-option-btn ${selected ? "active" : ""}`}
      onClick={() => onSelect(role)}
      type="button"
    >
      <span className="role-option-icon" style={{ color: meta.color }}>
        {meta.icon}
      </span>
      <span className="role-option-label">{meta.label}</span>
      <span className="role-option-desc">{meta.description}</span>
      {selected && <FiCheck className="role-option-check" />}
    </button>
  );
}

export default function Users({ toast }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const debounceRef = useRef();

  // Load users from API
  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      try {
        const data = await uGet("/users", {
          page,
          search,
          role: roleFilter !== "all" ? roleFilter : "",
        });
        setUsers(data.users || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      } catch (e) {
        toast(e.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, search, roleFilter, toast],
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(), 400);
    return () => clearTimeout(debounceRef.current);
  }, [load]);

  // Stats calculation
  const stats = useMemo(() => {
    const adminCount = users.filter((u) => u.role === "admin").length;
    const userCount = users.filter((u) => u.role === "user").length;
    const activeCount = users.filter((u) => u.isActive !== false).length;
    return { total: users.length, adminCount, userCount, activeCount };
  }, [users]);

  // Handle user update
  const handleSave = async () => {
    if (!editUser.name?.trim()) {
      toast("Name is required", "error");
      return;
    }
    setSaving(true);
    try {
      await uPut(`/users/${editUser.id}`, {
        name: editUser.name,
        phone: editUser.phone,
        role: editUser.role,
      });
      toast("User updated successfully ✓");
      setEditUser(null);
      load(true);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // Handle user deletion
  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    )
      return;
    try {
      await uDelete(`/users/${id}`);
      toast("User deleted successfully");
      load(true);
    } catch (e) {
      toast(e.message, "error");
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearch("");
    setRoleFilter("all");
    setPage(1);
  };

  // Refresh data
  const handleRefresh = () => {
    load(true);
  };

  // Get role icon for display
  const getRoleIcon = (role) => {
    if (role === "admin") return <FaCrown style={{ color: "#f59e0b" }} />;
    return <FaUser style={{ color: "#10b981" }} />;
  };

  return (
    <div className="users-container-enhanced">
      {/* Header Section */}
      <div className="users-header-enhanced">
        {/* <div className="header-title-section">
          <div className="header-icon">👥</div>
          <div>
            <h1 className="header-title">User Management</h1>
            <p className="header-subtitle">
              Manage customer accounts and permissions
            </p>
          </div>
        </div> */}
        <div className="header-stats">
          <div className="stat-card-mini">
            <FiUsersIcon />
            <div>
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Users</span>
            </div>
          </div>
          <div className="stat-card-mini admin">
            <FaCrown />
            <div>
              <span className="stat-value">{stats.adminCount}</span>
              <span className="stat-label">Admins</span>
            </div>
          </div>
          <div className="stat-card-mini user">
            <FaUser />
            <div>
              <span className="stat-value">{stats.userCount}</span>
              <span className="stat-label">Users</span>
            </div>
          </div>
          <div className="stat-card-mini active">
            <MdVerified />
            <div>
              <span className="stat-value">{stats.activeCount}</span>
              <span className="stat-label">Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="users-toolbar-enhanced">
        <div className="toolbar-left">
          <div className="search-wrapper-enhanced">
            <FiSearch className="search-icon-enhanced" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="search-input-enhanced"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="search-clear-enhanced"
              >
                <FiX />
              </button>
            )}
          </div>

          <button
            className={`filter-toggle-enhanced ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter /> Filter
          </button>

          <select
            className="role-filter-select-enhanced"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>

          {(search || roleFilter !== "all") && (
            <button
              onClick={clearFilters}
              className="clear-filters-btn-enhanced"
            >
              <FiX /> Clear
            </button>
          )}
        </div>

        <div className="toolbar-right">
          <button
            className="refresh-btn-enhanced"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <FiRefreshCw className={refreshing ? "spinning" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Result Count */}
      {!loading && (
        <div className="result-count-enhanced">
          <FiUsersIcon size={14} />
          <span>
            Showing {users.length} of {total} users
          </span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="users-loading-enhanced">
          <div className="loading-spinner-enhanced" />
          <span className="loading-text-enhanced">Loading users...</span>
        </div>
      )}

      {/* Desktop Table View */}
      {!loading && users.length > 0 && (
        <div className="users-table-wrapper-enhanced">
          <Table
            loading={loading}
            columns={[
              {
                key: "name",
                label: "User",
                render: (v, row) => (
                  <div className="user-cell-enhanced">
                    <div className={`user-avatar-enhanced ${row.role}`}>
                      {v?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="user-info-enhanced">
                      <span className="user-name-enhanceduser">{v}</span>
                      <span className="user-email-enhanced">
                        <FiMail size={10} /> {row.email}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                key: "phone",
                label: "Phone",
                render: (v) => (
                  <div className="phone-cell-enhanced">
                    <FiPhone size={12} />
                    <span>{v || "—"}</span>
                  </div>
                ),
              },
              {
                key: "role",
                label: "Role",
                render: (v) => (
                  <div className={`role-badge-enhanced ${v}`}>
                    {getRoleIcon(v)}
                    {v === "admin" ? "Admin" : "User"}
                  </div>
                ),
              },
              {
                key: "createdAt",
                label: "Joined",
                render: (v) => (
                  <div className="date-cell-enhanced">
                    <FiCalendar size={12} />
                    <span>{new Date(v).toLocaleDateString("en-IN")}</span>
                  </div>
                ),
              },
              {
                key: "isActive",
                label: "Status",
                render: (v) => (
                  <div
                    className={`status-badge-enhanced ${v !== false ? "active" : "inactive"}`}
                  >
                    {v !== false ? (
                      <MdVerified size={10} />
                    ) : (
                      <FiAlertCircle size={10} />
                    )}
                    {v !== false ? "Active" : "Inactive"}
                  </div>
                ),
              },
              {
                key: "id",
                label: "Actions",
                render: (v, row) => (
                  <div className="action-buttons-enhanced">
                    <button
                      className="action-icon-enhanced edit"
                      onClick={() => setEditUser({ ...row, id: row.id })}
                      title="Edit user"
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      className="action-icon-enhanced delete"
                      onClick={() => handleDelete(v)}
                      title="Delete user"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                ),
              },
            ]}
            data={users}
            rowKey="id"
          />
        </div>
      )}

      {/* Mobile Card View */}
      {!loading && users.length > 0 && (
        <div className="users-mobile-view-enhanced">
          {users.map((user) => (
            <UserCardMobile
              key={user.id}
              user={user}
              onEdit={setEditUser}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && users.length === 0 && (
        <EmptyState
          icon={FiUsersIcon}
          title="No users found"
          description={
            search || roleFilter !== "all"
              ? "Try adjusting your search or filter criteria"
              : "No users have registered yet"
          }
          action={
            search || roleFilter !== "all"
              ? { label: "Clear filters", onClick: clearFilters }
              : null
          }
        />
      )}

      {/* Pagination */}
      {!loading && users.length > 0 && pages > 1 && (
        <div className="pagination-wrapper-enhanced">
          <Pagination page={page} pages={pages} onChange={setPage} />
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal title="Edit User" onClose={() => setEditUser(null)} size="md">
          <div className="user-modal-enhanced">
            {/* User Preview */}
            <div className="user-preview-enhanced">
              <div className={`preview-avatar ${editUser.role}`}>
                {editUser.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="preview-info">
                <div className="preview-name">{editUser.name}</div>
                <div className="preview-email">
                  <FiMail size={12} /> {editUser.email}
                </div>
              </div>
              <div className={`role-badge-enhanced ${editUser.role}`}>
                {getRoleIcon(editUser.role)}
                {editUser.role === "admin" ? "Admin" : "User"}
              </div>
            </div>

            {/* Edit Form */}
            <div className="edit-form-enhanced">
              <div className="form-group-enhanced">
                <label className="form-label-enhanced">
                  <MdPerson size={12} /> Full Name
                </label>
                <div className="input-wrapper-enhanced">
                  <MdPerson className="input-icon-enhanced" />
                  <input
                    className="form-input-enhanced"
                    value={editUser.name}
                    onChange={(e) =>
                      setEditUser((p) => ({ ...p, name: e.target.value }))
                    }
                    placeholder="Enter full name"
                  />
                </div>
              </div>

              <div className="form-group-enhanced">
                <label className="form-label-enhanced">
                  <FiPhone size={12} /> Phone Number
                </label>
                <div className="input-wrapper-enhanced">
                  <FiPhone className="input-icon-enhanced" />
                  <input
                    className="form-input-enhanced"
                    value={editUser.phone || ""}
                    onChange={(e) =>
                      setEditUser((p) => ({ ...p, phone: e.target.value }))
                    }
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="form-group-enhanced">
                <label className="form-label-enhanced">
                  <FiMail size={12} /> Email Address
                </label>
                <div className="input-wrapper-enhanced">
                  <FiMail className="input-icon-enhanced" />
                  <input
                    className="form-input-enhanced"
                    value={editUser.email}
                    disabled
                    style={{ opacity: 0.6, cursor: "not-allowed" }}
                  />
                </div>
                <span className="field-hint-enhanced">
                  Email cannot be changed. Contact support for assistance.
                </span>
              </div>

              <div className="form-group-enhanced">
                <label className="form-label-enhanced">
                  <FaUserShield size={12} /> User Role
                </label>
                <div className="role-options-enhanced">
                  <RoleOption
                    role="user"
                    selected={editUser.role === "user"}
                    onSelect={(role) => setEditUser((p) => ({ ...p, role }))}
                  />
                  <RoleOption
                    role="admin"
                    selected={editUser.role === "admin"}
                    onSelect={(role) => setEditUser((p) => ({ ...p, role }))}
                  />
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="modal-footer-enhanced">
              <button
                className="btn-cancel-enhanced"
                onClick={() => setEditUser(null)}
              >
                Cancel
              </button>
              <button
                className="btn-save-enhanced"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <div className="spinner-small-enhanced" />
                ) : (
                  <FiCheck size={14} />
                )}
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
